import { createContext, useEffect, useState, type ReactNode } from 'react';
import type { AppUser } from '../types/api';
import * as authApi from '../lib/api/auth';
import { clearLocalChartState } from '../settings/clearChartState';

export interface AuthContextValue {
  user: AppUser | null;
  loading: boolean;
  checkingSession: boolean;
  status: string;
  error: string;
  signIn(identifier: string, password: string): Promise<void>;
  signUp(username: string, email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  clearMessage(): void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    authApi.getCurrentUser()
      .then((response) => {
        if (!cancelled) setUser(response.user);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setCheckingSession(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSignIn(identifier: string, password: string) {
    setLoading(true);
    setError('');
    setStatus('Signing in...');
    try {
      const response = await authApi.signIn(identifier, password);
      setUser(response.user);
      setStatus('Signed in. Loading charts...');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSignUp(username: string, email: string, password: string) {
    setLoading(true);
    setError('');
    setStatus('Creating account...');
    try {
      await authApi.signUp(username, email, password);
      setStatus('Account created. You can sign in now.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    setLoading(true);
    setError('');
    setStatus('Signing out...');
    try {
      clearLocalChartState();
      clearActiveAiRunState();
      await authApi.signOut();
      setUser(null);
      setStatus('Signed out.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign out failed');
    } finally {
      setLoading(false);
    }
  }

  function clearMessage() {
    setStatus('');
    setError('');
  }

  return (
    <AuthContext.Provider value={{ user, loading, checkingSession, status, error, signIn: handleSignIn, signUp: handleSignUp, signOut: handleSignOut, clearMessage }}>
      {children}
    </AuthContext.Provider>
  );
}

function clearActiveAiRunState() {
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (key?.startsWith('chartify:ai-active-run:')) localStorage.removeItem(key);
  }
}
